import {
  QueryFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { App } from 'antd';
import { handleError } from 'api';
import {
  AmenitiesApiService,
  AmenitiesResponse,
  GetAmenitiesParams,
} from 'features/amenities';

import { amenitiesQueryKeys } from './query-keys';

const { getAmenities } = AmenitiesApiService();

const getAmenitiesQuery = (
  params: GetAmenitiesParams,
  options?: UseQueryOptions<AmenitiesResponse>,
): {
  queryKey: QueryKey;
  queryFn: QueryFunction<AmenitiesResponse>;
  options?: UseQueryOptions<AmenitiesResponse>;
} => ({
  queryKey: amenitiesQueryKeys.list(params),
  queryFn: ({ signal }) => getAmenities(params, { config: { signal } }),
  ...options,
});

export const useGetAmenities = (
  params: GetAmenitiesParams,
  options?: UseQueryOptions<AmenitiesResponse>,
) => {
  const { message } = App.useApp();

  return useQuery<AmenitiesResponse, Error>(
    getAmenitiesQuery(params, {
      onError: (error: unknown) => {
        const uiMessage = handleError(error);

        message.error(uiMessage);
      },
      ...options,
    }),
  );
};

export const useGetAmenitiesInfiniteQuery = (
  params: GetAmenitiesParams,
  options?: UseInfiniteQueryOptions<AmenitiesResponse>,
) => {
  const { message } = App.useApp();

  return useInfiniteQuery({
    queryKey: amenitiesQueryKeys.list(params),
    queryFn: ({ signal, pageParam }) =>
      getAmenities(
        { ...params, page: pageParam || params.page },
        { config: { signal } },
      ),
    getNextPageParam: (lastPage) => {
      const hasMorePages = lastPage.pages > lastPage.page;

      return hasMorePages ? lastPage.page + 1 : undefined;
    },
    onError: (error: unknown) => {
      const uiMessage = handleError(error);

      message.error(uiMessage);
    },
    ...options,
  });
};
